import React, { useRef, useState } from 'react';
import { useI18n } from '../i18n';

// Files kept against a property or a work order — inspections, leases,
// warranties, permits.
//
// Shared by all four places a document shows up: the property sheet, the work
// order form, the work order itself, and Julian's lead card. One component
// because a document has to look and behave identically wherever it appears —
// the tech opening the move-out inspection from a job card and the manager
// opening it from the property must be opening the same thing, presented the
// same way.
//
// Deliberately NOT a preview. A 32-page inspection PDF is 3MB and gets opened
// on a phone, in a van, on cell data; rendering it inline would cost that
// download every time the screen is drawn. It opens in a new tab, on purpose,
// when someone actually asks for it.

// 'receipt' and 'video' are here because of what actually gets attached on a
// job: the supplier receipt for the parts, and the clip of the leak that no
// still photograph explains.
const KINDS = ['move-out-inspection', 'move-in-inspection', 'lease', 'notice', 'warranty', 'permit', 'invoice', 'receipt', 'photo', 'video', 'other'];

// A Cloudinary URL ends in a random id, so the stored filename is the only
// human label there is. Falls back to the extension rather than showing the id.
const labelFor = (d, t) => {
  if (d.name) return d.name;
  const ext = (d.url || '').split('.').pop();
  return ext && ext.length <= 5 ? `${t('doc.file')}.${ext}` : t('doc.file');
};

const isPdf = (d) => /\.pdf(\?|$)/i.test(d.url || '') || /\.pdf$/i.test(d.name || '');

const IconDoc = ({ pdf }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ flexShrink: 0, color: pdf ? 'var(--red)' : 'var(--slate)' }}>
    <path d="M14 2.5H7A1.5 1.5 0 0 0 5.5 4v16A1.5 1.5 0 0 0 7 21.5h10a1.5 1.5 0 0 0 1.5-1.5V7z" />
    <path d="M14 2.5V7h4.5" />
  </svg>
);

export default function DocumentList({
  documents = [],
  onAdd,            // (files) => Promise — omit to make the list read-only
  onRemove,         // (doc) => void — omit to hide the remove action
  units,            // property only: lets a file be filed against one unit
  title,
  emptyText,
}) {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState('move-out-inspection');
  const [unitId, setUnitId] = useState('');

  const pick = async (files) => {
    if (!files.length || !onAdd) return;
    setBusy(true);
    try {
      await onAdd(files, { kind, unitId: unitId || undefined });
    } finally {
      setBusy(false);
    }
  };

  const unitLabel = (id) => {
    const u = (units || []).find((x) => String(x._id) === String(id));
    return u ? (u.unitNumber ? `#${u.unitNumber}` : t('acct.wholeProperty')) : null;
  };

  return (
    <>
      {title && <div className="sec-label">{title}</div>}

      {documents.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)', padding: '4px 0 10px', lineHeight: 1.5 }}>
          {emptyText || t('doc.none')}
        </div>
      ) : (
        <div className="tp-rows" style={{ marginBottom: 12 }}>
          {documents.map((d, i) => (
            <div key={d._id || i} className="tp-row" style={{ cursor: 'default', alignItems: 'center' }}>
              <IconDoc pdf={isPdf(d)} />
              <div className="tp-row-main" style={{ marginLeft: 10 }}>
                <div className="tp-row-name">{labelFor(d, t)}</div>
                <div className="tp-row-meta">
                  {[
                    d.kind && d.kind !== 'other' ? t(`doc.kind.${d.kind}`, d.kind) : '',
                    d.unitId ? unitLabel(d.unitId) : '',
                    d.note,
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="tp-row-right" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                {/* New tab, not inline. See the note at the top of this file. */}
                <a
                  className="btn btn-outline btn-sm"
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  {t('doc.open')}
                </a>
                {onRemove && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => onRemove(d)}>
                    {t('doc.remove')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {onAdd && (
        <>
          <div className="form-row" style={{ marginBottom: 8 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>{t('doc.whatIsIt')}</label>
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                {KINDS.map((k) => <option key={k} value={k}>{t(`doc.kind.${k}`, k)}</option>)}
              </select>
            </div>
            {units && units.length > 0 && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{t('doc.whichUnit')}</label>
                <select value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                  <option value="">{t('doc.wholeBuilding')}</option>
                  {units.map((u) => (
                    <option key={u._id} value={u._id}>{u.unitNumber ? `#${u.unitNumber}` : t('acct.wholeProperty')}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <button className="btn btn-outline" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? t('doc.uploading') : t('doc.add')}
          </button>
          {/* Visually hidden, never display:none — Safari refuses to open a file
              picker for an input it considers not displayed. */}
          <input
            ref={fileRef}
            type="file"
            multiple
            // What phones actually produce, matched to what the server accepts
            // (server/upload.js): stills, video and PDFs alike. A format left
            // off here is one the picker greys out on a phone.
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.tif,.tiff,.mp4,.mov,.m4v,.webm,.3gp,.avi,.mkv"
            onChange={(e) => { pick(Array.from(e.target.files || [])); e.target.value = ''; }}
            style={{ position: 'absolute', opacity: 0, width: 1, height: 1, clip: 'rect(0 0 0 0)' }}
          />
        </>
      )}
    </>
  );
}
